import { useState } from "react";

import { PlusIcon, TagIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AVAILABLE_TAGS = ["auth", "payments", "mobile", "ui", "performance", "email", "sso", "security", "export", "notifications", "api", "database", "frontend", "backend", "accessibility", "testing"];

interface Props {
  selected: string[];
  onToggle: (tag: string) => void;
  disabled?: boolean;
}

export function TagSelector({ selected, onToggle, disabled }: Props) {
  const [custom, setCustom] = useState("");

  function handleAdd() {
    const trimmed = custom.trim().toLowerCase().replace(/\s+/g, "-");
    if (trimmed && !selected.includes(trimmed)) {
      onToggle(trimmed);
    }
    setCustom("");
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5">
        {AVAILABLE_TAGS.map((tag) => {
          const isSelected = selected.includes(tag);
          return (
            <button
              key={tag}
              type="button"
              onClick={() => onToggle(tag)}
              disabled={disabled}
              className={`flex items-center gap-1 border px-2 py-1 text-[10px] font-semibold tracking-wider uppercase transition-colors
                ${isSelected ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground"}
                ${disabled ? "pointer-events-none opacity-50" : ""}`}
            >
              <TagIcon className="size-2.5" />
              {tag}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Add custom tag…" value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())} disabled={disabled} className="h-8 text-xs" />
        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={disabled || !custom.trim()} className="shrink-0 h-8 px-2">
          <PlusIcon className="size-3.5" />
        </Button>
      </div>
      {selected.filter((t) => !AVAILABLE_TAGS.includes(t)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected
            .filter((t) => !AVAILABLE_TAGS.includes(t))
            .map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onToggle(tag)}
                disabled={disabled}
                className={`flex items-center gap-1 border border-primary/30 bg-primary/10 px-2 py-1 text-[10px] font-semibold tracking-wider uppercase text-primary transition-colors hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive
                  ${disabled ? "pointer-events-none opacity-50" : ""}`}
              >
                <TagIcon className="size-2.5 shrink-0" />
                {tag}
                <XIcon className="size-2.5 shrink-0" />
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
